// ============================================================================
// install.js — a kezdőképernyőre telepítés ("Add to Home Screen") kezelése.
//
// Két, egymástól ELTÉRŐ út van, mert a böngészők másképp működnek:
//
//  - Android / Chrome / Edge: a böngésző kiad egy `beforeinstallprompt`
//    eseményt, amit elmentve később egy SAJÁT gombbal ki lehet váltani a
//    natív telepítési párbeszédet. Ez a jobb élmény: egy kattintás.
//
//  - iOS / Safari: NINCS ilyen esemény és nincs programozott telepítés -
//    ott csak elmagyarázni tudjuk a felhasználónak a Megosztás →
//    "Kezdőképernyőhöz adás" lépést.
//
// Mindkét esetben csak akkor jelenítünk meg bármit, ha az alkalmazás NEM
// fut már telepített módban, és a felhasználó nem utasította el korábban.
// ============================================================================

const DISMISS_KEY = "lineage0_install_dismissed";

let deferredPrompt = null;
let onAvailableCallback = null;

export function isStandalone() {
  // Védett hívás: a matchMedia hiányozhat régi/szokatlan böngészőkben,
  // és egy itt eldobott kivétel az EGÉSZ alkalmazás indulását
  // megakadályozná - a telepítési felajánlás viszont csak kényelmi
  // funkció, sosem érhet ilyet.
  try {
    if (typeof window.matchMedia === "function" &&
        window.matchMedia("(display-mode: standalone)").matches) {
      return true;
    }
  } catch (e) {
    // szándékosan elnyelve - lásd fent
  }
  return window.navigator?.standalone === true;
}

export function isIos() {
  const ua = window.navigator?.userAgent || "";
  if (/iphone|ipad|ipod/i.test(ua)) return true;
  // iPadOS 13 óta az iPad alapból "Macintosh"-ként mutatkozik be; az
  // érintőpontok száma árulja el, hogy valójában iPad.
  return /Macintosh/.test(ua) && (window.navigator?.maxTouchPoints || 0) > 1;
}

/**
 * WebKit-alapú böngésző (Safari, és iOS-en MINDEN böngésző - a Chrome,
 * a Firefox és az Edge is, mert az Apple más motort nem enged).
 *
 * A Chrome, az Edge és az Opera asztali/Android változata Chromium-alapú:
 * ezeket ki kell zárni, mert az user agent stringjükben is szerepel a
 * "Safari" szó.
 */
export function isWebKit() {
  const ua = window.navigator?.userAgent || "";
  if (isIos()) return true;
  return /Safari/.test(ua) && !/Chrome|Chromium|Edg|OPR|Android/.test(ua);
}

function wasDismissed() {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === "1";
  } catch (e) {
    return false;
  }
}

export function markDismissed() {
  try {
    window.localStorage.setItem(DISMISS_KEY, "1");
  } catch (e) {
    // Nem kritikus - csak ebben a munkamenetben nem jelenik meg újra.
  }
  deferredPrompt = null;
}

/**
 * Megmondja, kell-e most telepítési felajánlást mutatni, és ha igen,
 * milyen módon: "prompt" (van natív párbeszéd) vagy "ios-hint"
 * (csak elmagyarázni tudjuk). null = ne mutassunk semmit.
 */
export function getInstallMode() {
  // Teljes védelem: ez a függvény MINDEN rendereléskor lefut, így egy itt
  // eldobott kivétel az egész felületet megakasztaná. A telepítési
  // felajánlás elmaradása viszont ártalmatlan.
  try {
    if (isStandalone() || wasDismissed()) return null;
    if (deferredPrompt) return "prompt";
    if (isIos()) return "ios-hint";
  } catch (e) {
    return null;
  }
  return null;
}

/** A natív telepítési párbeszéd kiváltása (csak "prompt" módban). */
export async function triggerInstall() {
  if (!deferredPrompt) return false;
  const promptEvent = deferredPrompt;
  deferredPrompt = null;
  promptEvent.prompt();
  try {
    const { outcome } = await promptEvent.userChoice;
    if (outcome === "dismissed") markDismissed();
    return outcome === "accepted";
  } catch (e) {
    return false;
  }
}

/**
 * Feliratkozás: a callback akkor fut le, ha a telepíthetőségi állapot
 * megváltozott (megjött a beforeinstallprompt, vagy az app települt),
 * hogy a felület újrarendereljen.
 */
export function onInstallAvailabilityChange(callback) {
  onAvailableCallback = callback;
}

window.addEventListener("beforeinstallprompt", (event) => {
  // Megakadályozzuk a böngésző saját, tolakodó sávját, és a saját,
  // lokalizált felajánlásunkat mutatjuk helyette.
  event.preventDefault();
  deferredPrompt = event;
  if (onAvailableCallback) onAvailableCallback();
});

window.addEventListener("appinstalled", () => {
  deferredPrompt = null;
  if (onAvailableCallback) onAvailableCallback();
});
